import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useState } from 'react'

import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import type { MenuOption } from '@/components/ui/MenuItem'
import MenuSingle from '@/components/ui/MenuSingle'
import MarketStatusPill from '@/components/ui/MarketStatusPill'
import RefreshRegion from '@/components/ui/RefreshRegion'
import SelectTrigger from '@/components/ui/SelectTrigger'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import UpdatingNote from '@/components/ui/UpdatingNote'
import { useLiveRuns, useLiveSignals } from '@/features/signals/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { formatDate, formatTime } from '@/lib/format'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import type { SignalBoard } from '@/schemas/signals'
import {
  liveSignalsSearchSchema,
  loadLiveSignals,
} from '@/screens/signals/LiveSignalsScreen.loader'
import SignalTileBoard from '@/screens/signals/components/SignalTileBoard'
import SignalsEmpty from '@/screens/signals/components/SignalsEmpty'
import SignalsLocked from '@/screens/signals/components/SignalsLocked'
import SignalsRouteError from '@/screens/signals/components/SignalsRouteError'
import { PickerSkeleton, StatusLineSkeleton } from '@/screens/signals/components/SignalsSkeletons'
import SignalsToolbar from '@/screens/signals/components/SignalsToolbar'
import { TileBoardSkeleton } from '@/screens/signals/components/SignalsTableSkeletons'
import { signalsRailProps } from '@/screens/signals/components/signals-rail'
import { formatRunTime, isSignalsLocked } from '@/screens/signals/lib/signals-ui'

const TITLE = 'Live Signals'

/** The strip names the page, so the header is the bare title (`sr-only` from `lg`). */
const HEADER = <TitleRow title={TITLE} />

/** `All signals` has no run id, so it needs a value of its own in a menu of strings. */
const ALL_RUNS = 'all'

/** The run line at the left, the market-status pill at the right (f02). */
const RUN_ROW = 'flex flex-wrap items-center justify-between gap-x-4 gap-y-2'

const EMPTY_NOTE = {
  green: 'No green signals in this run.',
  red: 'No red signals in this run.',
} as const

/*
 * The loaded page, block for block: the run row (the 24px pill sets its
 * height), the tabs with the run picker at their right and a board of tiles.
 * Board sizes are data, so twelve stand in.
 */
function LiveSignalsPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading live signals…"
      header={HEADER}
      {...signalsRailProps(TITLE)}
    >
      <div className={RUN_ROW}>
        <StatusLineSkeleton />
        <Skeleton className="h-6 w-28" />
      </div>
      {/* The run picker's own rough width, so the toolbar holds its place (f02). */}
      <TileBoardSkeleton toolbarEnd={<PickerSkeleton className="w-32" />} />
    </ScreenLayout>
  )
}

function LiveSignalsError(props: ErrorComponentProps) {
  return (
    <SignalsRouteError
      {...props}
      header={HEADER}
      pageTitle={TITLE}
      title="Live signals did not load"
    />
  )
}

export const Route = createFileRoute('/_app/market/signals/live')({
  validateSearch: liveSignalsSearchSchema,
  loader: loadLiveSignals,
  pendingComponent: LiveSignalsPending,
  errorComponent: LiveSignalsError,
  component: LiveSignalsScreen,
})

/**
 * "Last run at 02:48 PM, 19 Asoj 2083" — the intraday run's own time, in
 * Kathmandu and in BS (D5), with the market-status pill at its right as the
 * frame draws it. A poll that fails keeps the last run on screen and says so
 * here.
 */
function RunStatus({
  board,
  refreshFailed,
  picked,
}: {
  board: SignalBoard
  refreshFailed: boolean
  picked: boolean
}) {
  const run =
    board.asOf === null
      ? `Session of ${formatDate(board.date)}`
      : `${picked ? 'Run' : 'Last run'} at ${formatTime(board.asOf)}, ${formatDate(board.date)}`
  return (
    <div className={RUN_ROW}>
      <p className="text-body-xs text-muted-foreground-subtle">
        {run}
        {refreshFailed ? (
          <span className="text-destructive"> · Couldn’t refresh just now</span>
        ) : null}
      </p>
      <MarketStatusPill />
    </div>
  )
}

/**
 * "All signals ▾" at the tabs' right (f02, f15): every stock that signalled
 * at any run today, or one run's picture, by its time on the Kathmandu clock.
 * The options are `/live/runs/`, a SECONDARY read — the trigger draws without
 * it, and a `run` the list does not (yet) hold still reads as its own option
 * rather than as "All signals". Controlled by the URL's `run`. The mock's
 * `sz-select`: the accent-line edge while open, the menu's rows lifting to the
 * toast fill on hover.
 */
function RunPicker({
  run,
  onChange,
}: {
  run: number | null
  onChange: (run: number | null) => void
}) {
  const [open, setOpen] = useState(false)
  const runs = useLiveRuns().data ?? []
  const known = run === null || runs.some((entry) => entry.id === run)
  const options: readonly MenuOption[] = [
    { value: ALL_RUNS, label: 'All signals' },
    ...runs.map((entry) => ({ value: String(entry.id), label: formatRunTime(entry.at) })),
    ...(known ? [] : [{ value: String(run), label: 'Selected run' }]),
  ]
  const shown = options.find((option) => option.value === (run === null ? ALL_RUNS : String(run)))
  const label = shown?.label ?? 'All signals'

  return (
    <MenuSingle
      options={options}
      value={run === null ? ALL_RUNS : String(run)}
      onChange={(next) => {
        onChange(next === ALL_RUNS ? null : Number.parseInt(next, 10))
      }}
      open={open}
      onOpenChange={setOpen}
      label="Run"
      searchable={false}
      align="end"
      skin="tickLift"
      className="w-36"
      trigger={(triggerProps) => (
        <SelectTrigger
          {...triggerProps}
          aria-label={`Show signals from, currently ${label}`}
          label={label}
          open={open}
          openTone="line"
        />
      )}
    />
  )
}

/**
 * Signal Zone · Live signals — the intraday run's green and red boards for
 * the session in progress, strongest first, one side at a time as a grid of symbol tiles.
 *
 * MAIN read keyed on `?run=`, so `useQuery` + `keepPreviousData`, not suspense
 * (docs/route-loading.md, "Search params"): picking a run keeps the old board
 * on screen under RefreshRegion's refresh line until the new one lands. The
 * loader always has the entry URL's read. Only "All signals" polls, every
 * minute while NEPSE trades; a picked run is a past picture. A poll only
 * re-renders, so a failed poll keeps the last board.
 *
 * `data: null` is "nothing posted for this session yet" (B9: mm-be cannot
 * yet tell that from a run that found nothing), drawn as `SignalsEmpty`. A
 * 403 mid-session (enforcement switched on) is the locked state, in place. A
 * `?run=` mm-be does not hold (a stale link) is a region-local failure with
 * the picker still in the toolbar to go back to "All signals".
 *
 * `?side=` picks the tab on the client: both boards are already in hand, and
 * every tile shows (S3). "Signal at" is each row's `first_seen_at`
 * (owner 2026-10-13, overturning 2026-10-12's "no column": mm-be sends it now).
 */
function LiveSignalsScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const online = useOnline()
  const run = search.run ?? null
  const query = useLiveSignals(run)
  const view = resolveViewState(query, { online })
  const board = query.data
  const isUpdating = query.isPlaceholderData

  // The run picker sits at the tabs' right (the strip names the page, so the title row is bare).
  const picker = (
    <>
      <UpdatingNote when={isUpdating} />
      <RunPicker
        run={run}
        onChange={(next) => {
          void navigate({
            search: (prev) => ({ ...prev, run: next ?? undefined }),
          })
        }}
      />
    </>
  )

  if (isSignalsLocked(query.error)) {
    return (
      <ScreenLayout header={HEADER} {...signalsRailProps(TITLE)}>
        <SignalsLocked error={query.error} />
      </ScreenLayout>
    )
  }

  return (
    <ScreenLayout header={HEADER} {...signalsRailProps(TITLE)}>
      {board === undefined ? (
        view === 'failed' || view === 'offline' ? (
          <div>
            <SignalsToolbar end={picker} />
            <EmptyState
              kind={view}
              size="page"
              onRetry={() => void query.refetch()}
              retrying={query.isFetching}
              meta={view === 'failed' ? lastUpdatedMeta(query.dataUpdatedAt) : undefined}
              {...(view === 'failed' ? { title: 'Couldn’t load that run' } : {})}
            />
          </div>
        ) : (
          <TileBoardSkeleton toolbarEnd={<PickerSkeleton className="w-32" />} />
        )
      ) : board === null ? (
        <div>
          <SignalsToolbar end={picker} />
          <SignalsEmpty reason="live" />
        </div>
      ) : (
        <>
          <RunStatus board={board} refreshFailed={query.isError} picked={run !== null} />
          <RefreshRegion updating={isUpdating}>
            <SignalTileBoard
              from="live"
              rows={board}
              side={search.side}
              onSideChange={(side) => {
                void navigate({ search: (prev) => ({ ...prev, side }) })
              }}
              showSignalAt
              emptyNote={EMPTY_NOTE}
              toolbarEnd={picker}
            />
          </RefreshRegion>
        </>
      )}
    </ScreenLayout>
  )
}
